/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Subscription Management, Orders & Invoices Studio View (Task 10.6.4)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import {
  CreditCard,
  Sparkles,
  Receipt,
  ExternalLink,
} from 'lucide-react';
import { useTranslation } from '../i18n';
import { useSubscriptionStore } from '../stores/useSubscriptionStore';
import { CurrentSubscriptionCard } from '../components/billing/CurrentSubscriptionCard';
import { PlanPricingGrid } from '../components/billing/PlanPricingGrid';
import { OrdersHistoryTable } from '../components/billing/OrdersHistoryTable';
import { OrderReceiptModal } from '../components/billing/OrderReceiptModal';

interface SubscriptionPlansViewProps {
  initialTab?: 'plans' | 'orders';
}

export const SubscriptionPlansView: React.FC<SubscriptionPlansViewProps> = ({
  initialTab = 'plans',
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'plans' | 'orders'>(initialTab);
  const { openStripeCustomerPortal, isCustomerPortalLoading } = useSubscriptionStore();

  return (
    <div className="space-y-6 pb-20">
      {/* Studio Header Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-5 rounded-2xl bg-surface border border-surface-border shadow-xs">
        <div className="space-y-1">
          <div className="flex items-center gap-2.5">
            <div className="size-9 rounded-xl bg-primary/10 text-primary border border-primary/20 flex items-center justify-center">
              <CreditCard className="size-5" />
            </div>
            <h1 className="text-xl font-bold font-heading text-foreground">
              {t('subscription.title')}
            </h1>
            <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wide uppercase bg-primary/15 text-primary border border-primary/20">
              Billing Hub
            </span>
          </div>
          <p className="text-xs text-foreground-muted max-w-2xl">
            {t('subscription.subtitle')}
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            type="button"
            onClick={openStripeCustomerPortal}
            disabled={isCustomerPortalLoading}
            className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-surface hover:bg-surface-hover text-foreground border border-surface-border text-xs font-medium shadow-xs transition-colors disabled:opacity-50"
          >
            <CreditCard className="size-3.5 text-foreground-muted" />
            <span>{isCustomerPortalLoading ? t('subscription.portalLoading') : t('subscription.manageBilling')}</span>
            <ExternalLink className="size-3 opacity-70" />
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex items-center gap-2 p-1 rounded-xl bg-muted/60 border border-surface-border w-fit">
        <button
          type="button"
          onClick={() => setActiveTab('plans')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
            activeTab === 'plans'
              ? 'bg-surface text-foreground shadow-xs'
              : 'text-foreground-muted hover:text-foreground'
          }`}
        >
          <Sparkles className="size-3.5 text-primary" />
          <span>{t('subscription.tabPlans')}</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveTab('orders')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all ${
            activeTab === 'orders'
              ? 'bg-surface text-foreground shadow-xs'
              : 'text-foreground-muted hover:text-foreground'
          }`}
        >
          <Receipt className="size-3.5 text-foreground-muted" />
          <span>{t('subscription.tabOrders')}</span>
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'plans' ? (
        <div className="space-y-8 animate-in fade-in duration-200">
          <CurrentSubscriptionCard />
          <PlanPricingGrid />
        </div>
      ) : (
        <div className="space-y-6 animate-in fade-in duration-200">
          <OrdersHistoryTable />
        </div>
      )}

      {/* Receipt Modal */}
      <OrderReceiptModal />
    </div>
  );
};
